/**
 * providers/ninetailed/ninetailed-consent-bridge.tsx
 *
 * Bridges OneTrust consent into the Ninetailed SDK and re-resolves personalization
 * the moment consent is first granted.
 *
 * The SDK's privacy plugin starts in a STRICT blocked state (no events, no profile
 * fetch, no localStorage) and only unblocks when told the visitor consented, via
 * the global `window.ninetailed.consent(boolean)` the plugin registers at init.
 * This component reads our reactive `usePersonalizationConsent()` (OneTrust,
 * deny-by-default) and pushes the value into the SDK whenever it changes.
 *
 * Re-resolution on grant — why it lives here:
 *   Granting consent does two things inside the SDK, the second ASYNCHRONOUSLY:
 *     1. `consent(true)` writes the consent key (unblocks events immediately), and
 *     2. a `setItemEnd` listener then re-enables personalization *features* via a
 *        dispatched action (a microtask/tick later).
 *   The core sends `enabledFeatures` with every profile flush and the API only
 *   computes experiences for enabled features. So any `identify`/`page` fired in
 *   the SAME tick as `consent(true)` flushes BEFORE features are enabled -> the
 *   profile comes back with no experiences -> `<Experience>` is stuck on baseline
 *   until a full reload. The events blocked pre-consent are dropped, never
 *   replayed, so nothing self-heals.
 *   To fix this without a reload, on the denied->granted transition we re-fire
 *   `identify` (re-attaching the `isMember` trait) and `page` AFTER a short settle
 *   delay, guaranteeing the flush includes personalization features so the profile
 *   resolves the variant and `<Experience>` swaps live. TraitSync/TrackPages
 *   deliberately do NOT fire on the consent flip (they would race the enable);
 *   they own only ongoing login-state changes and navigation, which happen once
 *   consent is already settled.
 */

'use client';

import { useEffect, useRef } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-next';

import { usePersonalizationConsent } from '@/lib/consent/use-personalization-consent';
import { useUser } from '@/providers/UserProvider';
import { toMemberTrait } from '@/lib/ninetailed/toMemberTrait';
import { resolveIsMember } from '@/lib/ninetailed/resolveIsMember';

/**
 * Delay before re-firing events after consent is granted, giving the SDK's async
 * feature-enable (triggered by `consent(true)`) time to settle. The enable runs on
 * internal microtasks; this macrotask delay clears them comfortably.
 */
const CONSENT_SETTLE_MS = 200;

export function NinetailedConsentBridge() {
  const hasConsent = usePersonalizationConsent();
  const { page, identify } = useNinetailed();
  const { isLoggedIn, cachedInitials } = useUser();
  const isMember = resolveIsMember(isLoggedIn, cachedInitials);
  const wasConsentedRef = useRef(false);

  // Push the current consent value into the SDK. Because
  // `window.ninetailed.consent` is registered during the SDK's async init (which
  // may run after this mounts), poll briefly until the method exists, then apply.
  useEffect(() => {
    let cancelled = false;

    const apply = (): boolean => {
      const consent = window.ninetailed?.consent;
      if (typeof consent !== 'function') {
        return false;
      }
      consent(hasConsent);
      return true;
    };

    if (apply()) {
      return;
    }

    const interval = setInterval(() => {
      if (cancelled || apply()) {
        clearInterval(interval);
      }
    }, 200);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hasConsent]);

  // On the denied -> granted transition, re-resolve personalization after the
  // async feature-enable settles (see file header). Runs for both the first grant
  // of the session and a later grant via the OneTrust preference center; in both
  // cases the earlier mount-time events were blocked pre-consent and dropped.
  useEffect(() => {
    const wasConsented = wasConsentedRef.current;
    wasConsentedRef.current = hasConsent;

    if (!hasConsent || wasConsented) {
      return;
    }

    const timer = setTimeout(() => {
      identify('', toMemberTrait(isMember));
      page();
    }, CONSENT_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [hasConsent, identify, page, isMember]);

  return null;
}
